
import React, { createContext, useContext, useState, useEffect } from "react";

interface ConsentSettings {
  analytics: boolean;
  advertising: boolean;
  personalization: boolean;
  functionality: boolean;
  necessary: boolean; // This is always true as necessary cookies don't require consent
}

interface CookieConsentContextType {
  consentSettings: ConsentSettings;
  showBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (settings: ConsentSettings) => void;
  openSettings: () => void;
}

const defaultConsentSettings: ConsentSettings = {
  analytics: false,
  advertising: false,
  personalization: false,
  functionality: false,
  necessary: true,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consentSettings, setConsentSettings] = useState<ConsentSettings>(defaultConsentSettings);
  const [showBanner, setShowBanner] = useState<boolean>(false);

  // Load consent settings from localStorage on component mount
  useEffect(() => {
    const storedConsent = localStorage.getItem("cookieConsent");
    
    if (storedConsent) {
      try {
        const parsedConsent = JSON.parse(storedConsent);
        setConsentSettings(parsedConsent);
        setShowBanner(false);
      } catch (e) {
        console.error("Error parsing stored consent", e);
        setShowBanner(true);
      }
    } else {
      // No stored preference, show the banner
      setShowBanner(true);
    }
  }, []);

  // Update Google Analytics and other tracking based on consent (Consent Mode v2)
  useEffect(() => {
    // Prepare consent update object for gtag
    const consentUpdate: Record<string, string> = {
      'security_storage': 'granted', // Always granted for security purposes
    };

    // Set analytics consent
    consentUpdate.analytics_storage = consentSettings.analytics ? 'granted' : 'denied';
    
    // Set advertising consent
    consentUpdate.ad_storage = consentSettings.advertising ? 'granted' : 'denied';
    consentUpdate.ad_user_data = consentSettings.advertising ? 'granted' : 'denied';
    consentUpdate.ad_personalization = consentSettings.advertising ? 'granted' : 'denied';

    // Set personalization consent
    consentUpdate.personalization_storage = consentSettings.personalization ? 'granted' : 'denied';
    
    // Set functionality consent
    consentUpdate.functionality_storage = consentSettings.functionality ? 'granted' : 'denied';
    
    // Update Google consent
    window.gtag?.("consent", "update", consentUpdate);
    
  }, [consentSettings]);

  const saveConsentToStorage = (settings: ConsentSettings) => {
    localStorage.setItem("cookieConsent", JSON.stringify(settings));
  };

  const acceptAll = () => {
    const newSettings = {
      ...consentSettings,
      analytics: true,
      advertising: true,
      personalization: true,
      functionality: true,
    };
    setConsentSettings(newSettings);
    saveConsentToStorage(newSettings);
    setShowBanner(false);
  };

  const rejectAll = () => {
    const newSettings = {
      ...consentSettings,
      analytics: false,
      advertising: false,
      personalization: false,
      functionality: false,
    };
    setConsentSettings(newSettings);
    saveConsentToStorage(newSettings);
    setShowBanner(false);
  };

  const savePreferences = (settings: ConsentSettings) => {
    setConsentSettings(settings);
    saveConsentToStorage(settings);
    setShowBanner(false);
  };

  const openSettings = () => {
    setShowBanner(true);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consentSettings,
        showBanner,
        acceptAll,
        rejectAll,
        savePreferences,
        openSettings,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return context;
};

// This is to declare the gtag function for TypeScript
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}
